import { kStructuredCloneMarker } from './transfer.js';
import { ConfigurationError, NotFoundError, DeserializeError } from '../../../errors/shared/base.js';
import { Err, Ok } from '../../../result/shared/result.js';

const NAMESPACE = 'core.threads.transfer';
const registry = new Map();
/**
 * @private утилита для тестов
 */
function __clearRegistry() {
    registry.clear();
}
/**
 * Метод регистрации десериализаторов `IRestore` в глобальном реестре внутри отдельно взятого потока, другими
 * словами если в потоке нужно восстановить какой-то объект, то в этом потоке должен быть предварительно
 * зарегистрирован десериализатор
 *
 * Если десериализатор регистрируется повторно, то метод возвращает ошибку. Это сделано нарочно по нескольким
 * прчинам:
 *
 * 1. Регистрация десериализторов всегда должна происходить на старте потока или процесса, а не динамически
 *    по ходу работы программы
 * 2. Может сложиться так, что в проекте используются разные версии одной сущности (например, разные версии
 *    одной библиотеки), у которой совпадает уникальное имя. Если бы метод регистрации просто ингорировал это,
 *    то восстановление одного из объектов завершалось с ошибкой
 *
 * ```ts
 * import { register } from '@cian/core/threads/index.js';
 *
 * register(GenericError.restore()).unwrap();
 *
 * const genError = restore(
 *  new GenericError({ message: 'foo', domain: 'foo', type: 'Foo' }).toStructuredClone(),
 * ).unwrap();
 *
 * console.error(genError.printDetails());
 * ```
 *
 * @export
 * @template S - сериализованная структура
 * @template T - восстановленный объект
 * @param {IRestore<S, T>} restore - десериализатор
 * @return {EmptyResult<ConfigurationError>}
 */
function register(restore) {
    if (registry.has(restore[kStructuredCloneMarker])) {
        return Err(new ConfigurationError({
            message: `Повторная попытка зарегистрировать десериализатор ${restore[kStructuredCloneMarker]}`,
            domain: NAMESPACE + '.register',
        }));
    }
    registry.set(restore[kStructuredCloneMarker], restore);
    return Ok.Empty;
}
/**
 * Выполняет поиск в глобальном реестре десериализаторов и восстановливает экземпляр из структуры.
 * Если десериализатор не был предварительно зарегистрирован {@linkcode register}, то вернет ошибку
 *
 * @export
 * @template T - восстановленный экземпляр
 * @template S - сериализованный объект
 * @param {CloneStruct<S>} clone
 * @return {Result<T, NotFoundError>}
 */
function restore(clone) {
    const deserialaizer = registry.get(clone[0]);
    if (!deserialaizer) {
        return Err(new NotFoundError({
            message: `Нет подходящего десериализатора для ${clone[0]}`,
            domain: NAMESPACE + '.restore',
        }));
    }
    try {
        return Ok(deserialaizer.from(clone[1]));
    }
    catch (error) {
        return Err(new DeserializeError({
            message: 'Восстановление структуры завершилось с ошибкой ' + clone[0],
            domain: NAMESPACE + '.restore',
            details: error,
        }));
    }
}

export { __clearRegistry, register, restore };
//# sourceMappingURL=restoreRegistry.js.map
